import { ColumnType, Row } from "@asayinc/component-library";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";

import { CorporateAction } from "src/store/corporateAction/types";
import { CORPORATE_ACTION_LABEL } from "src/constants/labels";
import { maxDate, minDate } from "src/utils/compareDates";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export type ColumnDataHelpers = {
  getSort: (sortKey: string) => "asc" | "desc" | undefined;
  onOrderingChange: (data: unknown, sortKey: string) => void;
  onRowClick: (data: unknown) => void;
};

export type ApprovedColumnData = {
  type: ColumnType;
  issuer: ColumnType;
  scheduledStart: ColumnType;
  scheduledFinish: ColumnType;
  customers: ColumnType;
  elected: ColumnType;
  fails: ColumnType;
};

export function getColumns() {
  return [
    "type",
    "issuer",
    "scheduledStart",
    "scheduledFinish",
    "customers",
    "elected",
    "fails",
  ];
}

export function getColumnData({
  getSort,
  onOrderingChange,
  onRowClick,
}: ColumnDataHelpers): ApprovedColumnData {
  return {
    type: {
      clickHandler: onRowClick,
      sort: getSort("type"),
      sortFn: (data: unknown) => onOrderingChange(data, "type"),
      style: "text",
      title: "Type",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
      width: "22%",
    },
    issuer: {
      clickHandler: onRowClick,
      sort: getSort("issuer__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "issuer__name"),
      style: "text",
      title: "Issuer",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
      width: "22%",
    },
    scheduledStart: {
      clickHandler: onRowClick,
      sort: getSort("initial_start_scheduled_send_at"),
      sortFn: (data: unknown) =>
        onOrderingChange(data, "initial_start_scheduled_send_at"),
      style: "text",
      title: "Scheduled start",
      width: "11%",
    },
    scheduledFinish: {
      clickHandler: onRowClick,
      sort: getSort("initial_end_scheduled_send_at"),
      sortFn: (data: unknown) =>
        onOrderingChange(data, "initial_end_scheduled_send_at"),
      style: "text",
      title: "Scheduled finish",
      width: "11%",
    },
    customers: {
      clickHandler: onRowClick,
      sort: getSort("unique_customer_count"),
      sortFn: (data: unknown) =>
        onOrderingChange(data, "unique_customer_count"),
      style: "text",
      title: "Customers",
      width: "11%",
    },
    elected: {
      clickHandler: onRowClick,
      sort: getSort("election_count"),
      sortFn: (data: unknown) => onOrderingChange(data, "election_count"),
      style: "text",
      title: "Elected",
      width: "11%",
    },
    fails: {
      clickHandler: onRowClick,
      sort: getSort("efailure_count"),
      sortFn: (data: unknown) => onOrderingChange(data, "efailure_count"),
      style: "text",
      title: "Fails",
      width: "11%",
    },
  };
}

export function getRowData(mailings: CorporateAction[]): Row[] {
  return mailings.map((mailing, idx) => {
    const {
      id,
      securities,
      stats: {
        initialStartScheduledSendAt,
        initialWebhookStartScheduledSendAt,
        initialEndScheduledSendAt,
        initialWebhookEndScheduledSendAt,
        electionCount,
        initialErrorCount,
        uniqueCustomerCount,
      },
      type,
    } = mailing;

    const scheduledStart = minDate(
      initialStartScheduledSendAt,
      initialWebhookStartScheduledSendAt
    );

    const scheduledEnd = maxDate(
      initialEndScheduledSendAt,
      initialWebhookEndScheduledSendAt
    );

    return {
      id,
      data: { id, idx },
      rowData: {
        type: { content: CORPORATE_ACTION_LABEL[type] },
        issuer: { content: securities[0] ? securities[0].issuerName : "-" },
        scheduledStart: {
          content: scheduledStart
            ? dayjs.utc(scheduledStart).local().fromNow()
            : "-",
        },
        scheduledFinish: {
          content: scheduledEnd
            ? dayjs.utc(scheduledEnd).format("YYYY-MM-DD")
            : "-",
        },
        customers: { content: uniqueCustomerCount?.toLocaleString() || 0 },
        elected: { content: electionCount?.toLocaleString() || 0 },
        fails: { content: initialErrorCount?.toLocaleString() || 0 },
      },
    };
  });
}
